import { floorplanRoutes } from "@/router/routes/floorplan";
import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";
import { Ref, ref, SetupContext } from "@vue/composition-api";
import { authRoutes } from "@/router/routes/auth";
import { customerRoutes } from "@/router/routes/customer";
import { officeRoutes } from "@/router/routes/office";
import { floorRoutes } from "@/router/routes/floor";
import { reservationRoutes } from "@/router/routes/reservation";
import { miscRoutes } from "@/router/routes/misc";
import { userRoutes } from "@/router/routes/user";
import { featureRoutes } from "@/router/routes/feature";
import { teamRoutes } from "@/router/routes/team";
import { zoneRoutes } from "@/router/routes/zone";
import { reportRoutes } from "@/router/routes/reports";
import { deskRoutes } from "@/router/routes/desk";
import { eventbus } from "@/eventbus";
import { useTosti } from "@/plugins/vuetify-tosti";

Vue.use(VueRouter);

export interface StrictRouteConfig extends RouteConfig {
    meta: {
        label: string;
        title?: string;
        includeInNavigation?: boolean;
        includeInSubNavigation?: boolean;
        includeInBottomNavigation?: boolean;
        matchExact?: boolean;
        icon?: string;
        path?: string[];
        order?: number;
        hideNavbar?: boolean;

        // Picked up by the Okta plugin
        requiresAuth?: boolean;

        // Picked up by CASL can()
        // Todo: refactor as casl can take class constructors and things like Subject()
        requiresAbility?: [string, string];
    };
}

// @ts-ignore (for playground)
export const routes: Array<StrictRouteConfig> = [
    ...miscRoutes,

    ...customerRoutes,
    ...floorplanRoutes,
    ...officeRoutes,
    ...floorRoutes,
    ...zoneRoutes,
    ...userRoutes,
    ...teamRoutes,
    ...reservationRoutes,
    ...featureRoutes,
    ...authRoutes,
    ...reportRoutes,
    ...deskRoutes,
];

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,
});

const tosti = useTosti();

// Okta auth guard -> this one instantly redirects to Okta
// router.beforeEach(Vue.prototype.$auth.authRedirectGuard());

// This one leads to the login page
let emittedAuthLogin = false;
router.beforeEach(async (to, from, next) => {
    // const isAuthenticated = await Vue.prototype.$auth.isAuthenticated();
    // Okta SDK is falsely reporting TRUE for the above call EVEN when the token expired or doesn't exist.
    // This is the suggested workaround https://github.com/okta/okta-oidc-js/issues/721#issuecomment-615406979.
    // Pretty critical that this one should work.. /facepalm
    // Very hacky workaround. Let's do this sometime soon.
    // window.$auth = Vue.prototype.$auth;
    const isAuthenticated = !!(await Vue.prototype.$auth.getIdToken());

    // console.log("Is authenticated:", isAuthenticated);
    if (!isAuthenticated && to.matched.some((record) => record.meta.requiresAuth)) {
        // Navigate to custom login page
        // console.log("To login");
        emittedAuthLogin = false;
        next({ name: "login" });
    } else {
        if (to.matched.some((record) => record.meta.requiresAuth)) {
            // Todo: in the future, when we implement Okta auth ourselves, move this.
            // If we are allowed to proceed to a protected route, it means we're logged in
            if (!emittedAuthLogin) {
                eventbus.$emit("auth:login", await Vue.prototype.$auth.getUser());
                emittedAuthLogin = true;
            }
        }

        next();
    }
});

/**
 * Checks if a route requires a specific ability to be accessible.
 * When the ability check fails:
 * - if the user is loading a page from its browser url bar, redirect to the login page
 * - if the user is navigating to a restricted route, block the navigation
 *
 * NB: This guard should always be defined after the authentication one in order for
 * abilities to have been loaded first hand.
 *
 */
router.beforeEach(async (to, from, next) => {
    if (to.meta.requiresAbility && !Vue.prototype.$ability.can(...to.meta.requiresAbility)) {
        tosti.error({ message: "Unauthorized." });
        if (!from.name) return next({ name: "dashboard" });
        else return next(false);
    }

    next();
});

export function useRouter(context: SetupContext) {
    const router = (context.root as any).$router as VueRouter;
    const route = ref((context.root as any).$route as Route);

    return {
        router,
        routes,
        route,
    };
}

export default router;
